import React, { useState, useRef, useEffect } from 'react';
import { useUIStore } from '../../stores/uiStore';
import { useSendMessage, useCreateSession, useGetAssistantMessage } from '../../hooks/useChat';
import styles from './MessageInput.module.css';
import { useAuth } from '../../contexts/AuthContext';
import { userApi } from '../../services/api/endpoints/user';

interface MessageInputProps {
  sessionId?: string;
  onSessionCreated?: (sessionId: string) => void;
}

const MessageInput: React.FC<MessageInputProps> = ({ sessionId, onSessionCreated }) => {
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { setIsLoading, isLoading } = useUIStore();
  const { currentUser, setCurrentUser } = useAuth();
  const createSession = useCreateSession();
  const sendMessage = useSendMessage();
  const getAssistantMessage = useGetAssistantMessage();

  const isSessionActive = !!sessionId;

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [message]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!message.trim()) return;

    // Clear input after sending
    setMessage('');

    setIsLoading(true);

    try {
      if (!isSessionActive) {


        // Create a new session with the first message
        const result = await createSession.mutateAsync({
            content: message,
            role: 'user'
        });

        // Notify parent component about the new session
        if (onSessionCreated) {
          onSessionCreated(result.id);
        }

        // Get assistant response with the new session ID
        await getAssistantMessage.mutateAsync(result.id);

        await handleUserTokens()
      } else {
        // Send message to existing session
        await sendMessage.mutateAsync({
          sessionId,
          data: {
            chat_id: sessionId,
            content: message,
            role: 'user'
          }
        });

        // Get assistant response
        await getAssistantMessage.mutateAsync(sessionId);

        await handleUserTokens()
      }


    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserTokens = async () => {
    const user = await userApi.getUser()
    if (user.data.tokens_left !== undefined) {
      setCurrentUser(prev => ({
        ...prev!,
        tokens_left_current_period: user.data.tokens_left,
      }));
    }
  }

  const capitalize = (str: any): string =>
  {
    if(str)
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    else return ""
  }

  const isOutOfTokens = (currentUser?.tokens_left_current_period ?? 1) <= 0;

  return (
    <form className={styles.container} onSubmit={handleSubmit}>
      <div className={styles.textareaWrapper}>
        <span className={`${styles.quotaText} ${isOutOfTokens ? styles.quotaNoTokens : ''}`}>{capitalize(currentUser?.account_type)} | Tokens: {currentUser?.tokens_left_current_period ?? '...'} / {currentUser?.token_allowance ?? '...'}</span>
        <textarea
          ref={textareaRef}
          disabled={isOutOfTokens || isLoading}
          className={`${styles.textarea} ${isLoading ? styles.textareaDisabled : ''} ${isOutOfTokens ? styles.textareaNoTokens : ''}`}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={isOutOfTokens ? "No tokens left" : "Type your message..."}
          rows={1}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (!isLoading) {
                handleSubmit(e);
              }
            }
          }}
        />
        <button
          type="submit"
          className={styles.sendButton}
          disabled={!message.trim() || isLoading || isOutOfTokens}
          aria-label="Send message"
        >
          <svg
            className={styles.sendButtonIcon}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="22" y1="2" x2="11" y2="13"></line>
            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
          </svg>
        </button>
      </div>
    </form>
  );
};

export default MessageInput;